import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, RefreshCw, LogOut, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLeads = async (adminKey) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "X-Admin-Key": adminKey } });
      setLeads(res.data);
      setAuthed(true);
    } catch (err) {
      toast.error("Invalid admin key");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const login = (e) => {
    e.preventDefault();
    if (!key) return;
    fetchLeads(key);
  };

  const logout = () => {
    setAuthed(false);
    setKey("");
    setLeads([]);
  };

  useEffect(() => {
    if (authed) fetchLeads(key);
    // eslint-disable-next-line
  }, []);

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-charcoal px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-2xl border border-cream/15 bg-cream/5 p-8 backdrop-blur-sm"
        >
          <h1 className="font-serif text-2xl font-bold text-cream">Lead Dashboard</h1>
          <p className="mt-2 text-sm text-cream/60">Enter the admin key to view consultation requests.</p>
          <Input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            className="mt-6 border-cream/20 bg-cream/10 text-cream placeholder:text-cream/40 focus-visible:ring-sage"
          />
          <Button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="mt-4 h-11 w-full bg-sage font-semibold text-white hover:bg-blush hover:text-charcoal"
          >
            {loading ? "Checking..." : "View Leads"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-charcoal/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <h1 className="font-serif text-2xl font-bold">Consultation Leads</h1>
          <div className="flex items-center gap-3">
            <Button
              data-testid="refresh-leads"
              variant="outline" size="sm" onClick={() => fetchLeads(key)}
              className="gap-2"
            >
              <RefreshCw size={15} /> Refresh
            </Button>
            <Button data-testid="admin-logout" variant="ghost" size="sm" onClick={logout} className="gap-2">
              <LogOut size={15} /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <p className="mb-6 text-sm font-semibold text-charcoal/60" data-testid="leads-count">
          {leads.length} total request{leads.length !== 1 ? "s" : ""}
        </p>

        {leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-charcoal/15 py-20 text-charcoal/50">
            <Inbox size={40} />
            <p className="mt-4 font-medium">No leads yet</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {leads.map((l) => (
              <div
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                className="rounded-xl border border-charcoal/10 bg-white p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-xl font-bold">{l.name}</h3>
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-charcoal/70">
                      <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 hover:text-sage"><Phone size={14} /> {l.phone}</a>
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-sage"><Mail size={14} /> {l.email}</a>
                    </div>
                  </div>
                  <span className="text-xs text-charcoal/40">
                    {new Date(l.created_at).toLocaleString()}
                  </span>
                </div>
                {l.service && (
                  <span className="mt-3 inline-block rounded-full bg-sage/15 px-3 py-1 text-xs font-semibold text-sage">
                    {l.service}
                  </span>
                )}
                {l.message && <p className="mt-3 text-sm leading-relaxed text-charcoal/70">{l.message}</p>}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
